import { Box, DialogContent as MuiDialogContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ActivityData, DocumentData, Page } from '@ysura/common';
import { useEffect, useRef, useState } from 'react';

import { useInteraction } from '@/hooks';
import { useGetPersonDataForMediaInjectionQuery } from '@/services/graphql/hooks';
import { parsePersonConnection } from '@/services/graphql/parsers';
import { transformAttendeeDataForMediaInjection } from '@/services/media/helper';
import { filterNonNull } from '@/utils/dataHelpers/filters';
import { PointerContainer } from '@/views/Interaction/Remote/Room/MediaSharing/PointerContainer';

type HTMLZIPFileProps = {
  activity?: ActivityData;
  data: ArrayBuffer;
  documentMetadata: DocumentData;
  initialPage?: Page;
  onClose: VoidFunction;
  isInInteraction?: boolean;
  isFullScreenShown?: boolean;
  isReadOnlyMode?: boolean;
};

export const HTMLZIPFile = ({
  activity,
  data,
  documentMetadata,
  isInInteraction = false,
  initialPage = { pageId: 'INITIAL_PAGE', indexh: 0, indexv: 0 },
  isReadOnlyMode = false,
}: HTMLZIPFileProps) => {
  const { changePage, broadcastHTMLEvent } = useInteraction();
  const [startTimeInPage, setStartTimeInPage] = useState<Date>(new Date());
  const [currentPageNumber, setCurrentPageNumber] = useState(initialPage);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const attendee = activity?.attendees?.[0]?.person;
  const attendeeIds =
    activity?.attendees?.map((attendee) => attendee?.person?.oid) ?? [];

  const { data: attributesData } = useGetPersonDataForMediaInjectionQuery({
    variables: { personIds: filterNonNull(attendeeIds) },
    skip: !attendeeIds.length,
  });

  useEffect(() => {
    const onLoad = () => {
      setIsIframeLoaded(true);
    };

    const iframe = iframeRef.current;
    iframe?.addEventListener('load', onLoad);

    return () => {
      iframe?.removeEventListener('load', onLoad);
    };
  }, []);

  useEffect(() => {
    if (isIframeLoaded && attendee && attributesData) {
      const personDataForMedia = parsePersonConnection(attributesData.persons);
      console.log('Posting person data to iframe', personDataForMedia);
      const transformedData =
        transformAttendeeDataForMediaInjection(personDataForMedia);

      const msg = {
        namespace: 'ysura',
        eventType: 'personData',
        payload: { persons: transformedData },
      };
      iframeRef?.current?.contentWindow?.postMessage(msg, '*');
      // eslint-disable-next-line
      // @ts-ignore
      broadcastHTMLEvent(msg);
    }
  }, [isIframeLoaded, attendee, attributesData, broadcastHTMLEvent]);

  useEffect(() => {
    const messageListener = (event: MessageEvent) => {
      if (!(typeof event.data === 'string')) {
        return;
      }

      const eventData = JSON.parse(event.data);

      if (eventData.namespace !== 'ysura') {
        return;
      }

      if (eventData.eventType === 'pageChanged' && isInInteraction) {
        const nextPageId = eventData.payload.pageId;
        if (currentPageNumber.pageId === nextPageId) {
          return;
        }

        const nextPage = {
          ...initialPage,
          pageId: eventData.payload.pageId,
        };

        changePage(
          nextPage,
          currentPageNumber,
          new Date().getTime() - startTimeInPage?.getTime()
        );

        setStartTimeInPage(new Date());
        setCurrentPageNumber(nextPage);
      }

      broadcastHTMLEvent(eventData);
    };

    window.addEventListener('message', messageListener);

    return () => {
      window.removeEventListener('message', messageListener);
    };
  }, [
    broadcastHTMLEvent,
    changePage,
    currentPageNumber,
    initialPage,
    isInInteraction,
    startTimeInPage,
  ]);

  useEffect(() => {
    const renderFile = async (fileContent: ArrayBuffer) => {
      const reg = await navigator.serviceWorker.ready;

      // First check if we have cached files
      const cacheChannel = new MessageChannel();
      const cacheCheckPromise = new Promise<{
        cached: boolean;
        cacheKey?: string;
      }>((resolve) => {
        cacheChannel.port1.onmessage = (event) => {
          resolve(event.data);
        };
      });

      const mediaInfo = { documentId: documentMetadata.id };

      reg.active?.postMessage(
        {
          type: 'CHECK_ZIP_CACHE',
          mediaInfo: mediaInfo,
        },
        [cacheChannel.port2]
      );

      const cacheResult = await cacheCheckPromise;

      if (cacheResult.cached) {
        if (iframeRef.current) {
          iframeRef.current.src = '/zip/index.html';
        }
      } else {
        try {
          const extractChannel = new MessageChannel();

          reg.active?.postMessage(
            {
              type: 'EXTRACT_ZIP_FROM_ARRAYBUFFER',
              mediaInfo: mediaInfo,
              arrayBuffer: fileContent,
            },
            [fileContent, extractChannel.port2]
          );

          extractChannel.port1.onmessage = (event) => {
            if (event.data.success && iframeRef.current) {
              iframeRef.current.src = '/zip/index.html';
            } else {
              throw new Error(event.data.error);
            }
          };
        } catch (error) {
          console.error('Error downloading ZIP file:', error);
          throw error;
        }
      }
    };

    if (data) {
      renderFile(data);
    }
  }, [data, documentMetadata.id]);

  const renderIframe = () => {
    return (
      <Iframe
        ref={iframeRef}
        title="HTML"
        isReadOnlyMode={isReadOnlyMode}
        data-testid="HTMLZIP_iframe"
      />
    );
  };

  return (
    <Wrapper>
      <DialogContent>
        {isInInteraction ? (
          <PointerContainer
            iframeRef={iframeRef as React.RefObject<HTMLIFrameElement>}
          >
            {renderIframe()}
          </PointerContainer>
        ) : (
          renderIframe()
        )}
      </DialogContent>
    </Wrapper>
  );
};

const Wrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
});

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  padding: theme.spacing(2),
}));

const Iframe = styled('iframe', {
  shouldForwardProp: (prop) => prop !== 'isReadOnlyMode',
})<{ isReadOnlyMode: boolean }>(({ theme, isReadOnlyMode }) => ({
  width: '100%',
  height: '100%',
  borderRadius: Number(theme.shape.borderRadius) * 2,
  pointerEvents: isReadOnlyMode ? 'none' : 'auto',
}));
